import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { orderBy, uniq } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { TermExplorerActions } from './term-explorer.actions';
import { TERM_DEAL_COUNT_LIMIT } from '../../features/deal/constants/limits';
import { TermExplorerService } from '../../features/deal/services/term-explorer.service';
import { getTransactionIdFromDocumentTransactionLibraryId } from '../../features/document/constants/document-helper';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DealActions } from '../deal/deal.actions';
import { DealSelectors } from '../deal/deal.selectors';
import { DealFilterActions } from '../deal-filter/deal-filter.actions';
import { DealFilterSelectors } from '../deal-filter/deal-filter.selectors';
import { RootState } from '../root-state.model';

@Injectable()
export class TermExplorerEffects {
  private service = inject(TermExplorerService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);

  private readonly termExplorerChunkSize = 2000;

  loadFinalDocumentTerms$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TermExplorerActions.LoadFinalDocumentTerms),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        if (!action.documentId || !store.deal.transaction?.transactionId) {
          return of(TermExplorerActions.CleanFinalDocumentTerms());
        }

        return this.service
          .getDocumentDefinedTerms(action.documentId, store.deal.transaction?.transactionId)
          .pipe(
            map((list) => TermExplorerActions.LoadFinalDocumentTermsSuccess({ list })),
            reportEffectError(TermExplorerActions.LoadFinalDocumentTermsFail)
          );
      })
    )
  );

  triggerOccurenceLoad$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TermExplorerActions.LoadFinalDocumentTermsSuccess,
        TermExplorerActions.LoadOriginalDocumentTermsSuccess,
        DealFilterActions.UseFilterSuccess
      ),
      withLatestFrom(
        this.store.select(DealFilterSelectors.filteredTransactions),
        this.store.select(DealSelectors.deal)
      ),
      filter(([action, transactions, deal]) => {
        return (
          !!transactions &&
          deal &&
          window.location.pathname.endsWith(
            `/transactions/${deal.libraryId}/${deal.managerId}/${deal.transactionShortName}`
          )
        );
      }),
      map(([action, transactions]) => {
        return TermExplorerActions.LoadDefinedTermOccurences({
          transactionIds: orderBy(transactions, (t) => t.transactionDisplayClosingDate, 'desc')
            .slice(0, TERM_DEAL_COUNT_LIMIT)
            .map((t) => t.transactionId),
        });
      })
    )
  );

  recalculate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TermExplorerActions.Recalculate),
      withLatestFrom(
        this.store.select(DealFilterSelectors.filteredTransactions),
        this.store.select(DealSelectors.deal)
      ),
      filter(([action, transactions, deal]) => {
        return (
          !!transactions &&
          deal &&
          window.location.pathname.endsWith(
            `/transactions/${deal.libraryId}/${deal.managerId}/${deal.transactionShortName}`
          )
        );
      }),
      map(([action, transactions]) => {
        let transactionList = orderBy(transactions, (t) => t.transactionDisplayClosingDate, 'desc');
        transactionList = action.withoutLimit
          ? transactionList
          : transactionList.slice(0, TERM_DEAL_COUNT_LIMIT);
        return TermExplorerActions.LoadDefinedTermOccurences({
          transactionIds: transactionList.map((t) => t.transactionId),
        });
      })
    )
  );

  loadDefinedTermOccurences$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TermExplorerActions.LoadDefinedTermOccurences),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.termExplorer.transactionIdsToDownload?.length),
      map(([action, store]) =>
        TermExplorerActions.LoadDefinedTermOccurenceChunk({
          transactionIds: store.termExplorer.transactionIdsToDownload.slice(
            0,
            this.termExplorerChunkSize
          ),
        })
      )
    )
  );

  loadOccurences$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TermExplorerActions.LoadDefinedTermOccurenceChunk),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.service
          .getDefinedTermOccurence(
            [store.termExplorer.finalDocumentId, store.termExplorer.originalDocumentId].filter(
              (id) => !!id
            ),
            [
              store.deal.transaction?.transactionId,
              getTransactionIdFromDocumentTransactionLibraryId(
                store.deal.originalDocumenTransactionLibrarytId
              ),
            ].filter((id) => !!id),

            uniq([...(action.transactionIds || [])]).filter((v) => !!v)
          )
          .pipe(
            map((occurences) =>
              TermExplorerActions.LoadDefinedTermOccurenceChunkSuccess({
                occurences,
                transactionIds: action.transactionIds,
              })
            ),
            reportEffectError(TermExplorerActions.LoadDefinedTermOccurenceFail)
          );
      })
    )
  );

  loadDefinedTermOccurenceChunkSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TermExplorerActions.LoadDefinedTermOccurenceChunkSuccess),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.termExplorer.transactionIdsToDownload?.length),
      map(([action, store]) =>
        TermExplorerActions.LoadDefinedTermOccurenceChunk({
          transactionIds: store.termExplorer.transactionIdsToDownload.slice(
            0,
            this.termExplorerChunkSize
          ),
        })
      )
    )
  );

  loadOriginalDocumentTerms$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TermExplorerActions.LoadOriginalDocumentTerms),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        const transactionId = getTransactionIdFromDocumentTransactionLibraryId(
          store.deal.originalDocumenTransactionLibrarytId
        );

        if (!action.documentId || !transactionId) {
          return of(TermExplorerActions.CleanOriginalDocumentTerms());
        }

        return this.service.getDocumentDefinedTerms(action.documentId, transactionId).pipe(
          map((list) => TermExplorerActions.LoadOriginalDocumentTermsSuccess({ list })),
          reportEffectError(TermExplorerActions.LoadOriginalDocumentTermsFail)
        );
      })
    )
  );

  loadDeal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDeal),
      map((action) => TermExplorerActions.CleanOriginalDocumentTerms())
    )
  );
}
